// АНИМ.2: общие визуальные подкомпоненты для всех 5 сцен.
//
// Координатная сетка: 720 × 1280 (внутри Stage). x=360 — центр.
// Все компоненты — pure (без useState/useEffect), все значения через T().

import React from 'react';
import { T, Easing, useTime } from '../../components/animation/animations';
import type { LeagueUiDef } from './leaguesData';

// ── DataTraces ───────────────────────────────────────────────────
// Горизонтальные «пакеты данных» — несколько светящихся линий,
// движущихся слева направо со сдвигом фазы.
export function DataTraces({
  color, count = 14, opacity = 0.4, speed = 60
}: { color: string; count?: number; opacity?: number; speed?: number }) {
  const t = useTime();
  const lines = React.useMemo(
    () => Array.from({ length: count }, (_, i) => ({
      y: 60 + ((i * 73) % 1140),       // pseudo-random vertical distribution
      w: 80 + ((i * 17) % 220),        // 80..300px wide
      offset: (i * 47) % 720           // start offset
    })),
    [count]
  );
  return (
    <div style={{
      position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden'
    }}>
      {lines.map((l, i) => {
        const x = ((l.offset + t * speed) % (720 + l.w + 200)) - l.w - 100;
        return (
          <div key={i} style={{
            position: 'absolute',
            left: x, top: l.y, width: l.w, height: 1,
            background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
            opacity
          }} />
        );
      })}
    </div>
  );
}

// ── LeagueBackdrop ───────────────────────────────────────────────
// Два радиал-градиента (старая лига → новая) с кроссфейдом.
export function LeagueBackdrop({
  fromL, toL, swapAt = 1.0
}: { fromL: LeagueUiDef; toL: LeagueUiDef; swapAt?: number }) {
  const t = useTime();
  const blend = T(t, swapAt - 0.3, swapAt + 0.3, 0, 1, Easing.easeInOutCubic);
  const grad = (color: string) =>
    `radial-gradient(ellipse at 50% 40%, ${hexA(color, 0.28)} 0%, ${hexA(color, 0.10)} 30%, #07070B 70%)`;
  return (
    <>
      <div style={{
        position: 'absolute', inset: 0,
        background: grad(fromL.color), opacity: 1 - blend
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        background: grad(toL.color), opacity: blend
      }} />
    </>
  );
}

// ── LadderHologram ───────────────────────────────────────────────
// Изометрическая лестница 5 ступеней, фигурка-аватар на текущей ступени.
// fromStep / toStep — позиция фигурки (0..4).
export function LadderHologram({
  fromStep, toStep, color, startAt = 0.35, moveAt = 1.0, y = 560
}: {
  fromStep: number; toStep: number; color: string;
  startAt?: number; moveAt?: number; y?: number;
}) {
  const t = useTime();
  const op = T(t, startAt, startAt + 0.45, 0, 1, Easing.easeOutCubic);
  const step = T(t, moveAt, moveAt + 0.5, fromStep, toStep, Easing.easeOutCubic);

  // 5 ступеней, каждая 80px высоты, общая высота 400px.
  const stairCount = 5;
  const stepW = 240;
  const stepH = 18;
  const gap = 50;     // вертикальный gap между ступенями
  const baseY = y + (stairCount - 1) * gap / 2;

  return (
    <div style={{
      position: 'absolute', left: 360 - stepW / 2, top: y - 200,
      width: stepW, height: 420, opacity: op, pointerEvents: 'none'
    }}>
      {Array.from({ length: stairCount }, (_, i) => {
        const sy = baseY - i * gap;
        const isActive = Math.abs(step - i) < 0.5;
        const colA = isActive ? color : hexA(color, 0.25);
        return (
          <React.Fragment key={i}>
            {/* верхний ромб */}
            <div style={{
              position: 'absolute', left: 0, top: sy,
              width: stepW, height: stepH,
              background: hexA(colA, isActive ? 0.4 : 0.15),
              border: `1px solid ${colA}`,
              transform: 'skewX(-30deg)',
              boxShadow: isActive ? `0 0 18px ${colA}` : 'none'
            }} />
            {/* цифра уровня */}
            <div style={{
              position: 'absolute', left: stepW + 10, top: sy - 4,
              color: colA, fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11, letterSpacing: '0.1em'
            }}>L{i + 1}</div>
          </React.Fragment>
        );
      })}
      {/* Фигурка-аватар */}
      <FigureSilhouette
        color={color}
        x={stepW / 2}
        y={baseY - step * gap - 36}
      />
    </div>
  );
}

function FigureSilhouette({ color, x, y }: { color: string; x: number; y: number }) {
  return (
    <svg width="40" height="44" viewBox="0 0 40 44" style={{
      position: 'absolute', left: x - 20, top: y, filter: `drop-shadow(0 0 6px ${color})`
    }}>
      <circle cx="20" cy="10" r="7" fill={color} opacity="0.9" />
      <path d="M 7 42 L 7 26 Q 7 18 20 18 Q 33 18 33 26 L 33 42 Z" fill={color} opacity="0.7" />
    </svg>
  );
}

// ── LeagueNameSwap ───────────────────────────────────────────────
// Старое имя уезжает влево с RGB-split, новое прилетает справа с scanline.
export function LeagueNameSwap({
  fromL, toL, swapAt = 0.8, y = 200, isEn = false
}: { fromL: LeagueUiDef; toL: LeagueUiDef; swapAt?: number; y?: number; isEn?: boolean }) {
  const t = useTime();
  const oldOp = T(t, swapAt, swapAt + 0.5, 1, 0, Easing.easeInCubic);
  const oldX = T(t, swapAt, swapAt + 0.5, 0, -200, Easing.easeInCubic);
  const oldSplit = T(t, swapAt, swapAt + 0.4, 2, 8, Easing.easeInCubic);
  const newOp = T(t, swapAt + 0.4, swapAt + 1.0, 0, 1, Easing.easeOutCubic);
  const newX = T(t, swapAt + 0.4, swapAt + 1.0, 200, 0, Easing.easeOutCubic);
  const scanOp = T(t, swapAt + 0.4, swapAt + 0.9, 1, 0, Easing.easeOutCubic);

  const oldName = isEn ? fromL.en : fromL.ru;
  const newName = isEn ? toL.en : toL.ru;

  return (
    <div style={{
      position: 'absolute', left: 0, right: 0, top: y,
      textAlign: 'center', pointerEvents: 'none'
    }}>
      <div style={{
        position: 'absolute', left: 0, right: 0,
        transform: `translateX(${oldX}px)`, opacity: oldOp,
        color: fromL.color,
        fontFamily: "'Chakra Petch', sans-serif",
        fontSize: 34, fontWeight: 700, letterSpacing: '0.08em',
        textShadow: `${-oldSplit}px 0 0 #FF3D8F, ${oldSplit}px 0 0 #3DF0FF`
      }}>{oldName}</div>
      <div style={{
        position: 'absolute', left: 0, right: 0,
        transform: `translateX(${newX}px)`, opacity: newOp,
        color: toL.color,
        fontFamily: "'Chakra Petch', sans-serif",
        fontSize: 34, fontWeight: 700, letterSpacing: '0.08em'
      }}>
        {newName}
        {/* scanline-маска поверх нового имени */}
        <div style={{
          position: 'absolute', inset: 0, opacity: scanOp,
          background: `repeating-linear-gradient(0deg, transparent 0 2px, ${hexA(toL.color, 0.4)} 2px 4px)`,
          mixBlendMode: 'screen'
        }} />
      </div>
    </div>
  );
}

// ── HeroTitle ────────────────────────────────────────────────────
// Крупный заголовок с RGB-split, settle на settleAt.
export function HeroTitle({
  at, text, color, size = 56, y = 760
}: { at: number; text: string; color: string; size?: number; y?: number }) {
  const t = useTime();
  const op = T(t, at, at + 0.35, 0, 1, Easing.easeOutCubic);
  const split = T(t, at, at + 0.35, 6, 0, Easing.easeOutCubic) +
                T(t, at + 0.4, at + 1.5, 0, 0, Easing.linear);
  return (
    <div style={{
      position: 'absolute', left: 0, right: 0, top: y,
      textAlign: 'center', opacity: op, pointerEvents: 'none',
      color, fontFamily: "'Chakra Petch', sans-serif",
      fontSize: size, fontWeight: 700, letterSpacing: '0.08em',
      textShadow: `${-split}px 0 0 #FF3D8F, ${split}px 0 0 #3DF0FF, 0 0 24px ${hexA(color, 0.7)}`
    }}>{text}</div>
  );
}

// ── RewardCascade ────────────────────────────────────────────────
// Горизонтальный strip из «чипов» наград. Каждый чип появляется со
// сдвигом stagger.
export function RewardCascade({
  rewards, startAt = 2.3, stagger = 0.18, y = 900
}: { rewards: RewardChipData[]; startAt?: number; stagger?: number; y?: number }) {
  return (
    <div style={{
      position: 'absolute', left: 0, right: 0, top: y,
      display: 'flex', justifyContent: 'center', gap: 14,
      pointerEvents: 'none', flexWrap: 'wrap', padding: '0 40px'
    }}>
      {rewards.map((r, i) => <RewardChip key={i} reward={r} at={startAt + i * stagger} />)}
    </div>
  );
}

export interface RewardChipData {
  label: string;
  color: string;
  icon?: string;
}

function RewardChip({ reward, at }: { reward: RewardChipData; at: number }) {
  const t = useTime();
  const op = T(t, at, at + 0.4, 0, 1, Easing.easeOutCubic);
  const ty = T(t, at, at + 0.4, 24, 0, Easing.easeOutCubic);
  return (
    <div style={{
      opacity: op, transform: `translateY(${ty}px)`,
      padding: '10px 16px',
      background: `${hexA(reward.color, 0.12)}`,
      border: `1px solid ${reward.color}`,
      color: reward.color,
      fontFamily: "'JetBrains Mono', monospace", fontSize: 14,
      letterSpacing: '0.05em',
      clipPath: 'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
      backdropFilter: 'blur(8px)',
      boxShadow: `0 0 18px ${hexA(reward.color, 0.4)}`
    }}>
      {reward.icon && <span style={{ marginRight: 8 }}>{reward.icon}</span>}
      {reward.label}
    </div>
  );
}

// ── CTAForward ───────────────────────────────────────────────────
// Главная кнопка внизу. fade-in после контента.
export function CTAForward({
  at = 3.2, label = 'ВПЕРЁД', color = '#3DF0FF', onClick
}: { at?: number; label?: string; color?: string; onClick?: () => void }) {
  const t = useTime();
  const op = T(t, at, at + 0.4, 0, 1, Easing.easeOutCubic);
  const interactable = op > 0.8;
  return (
    <div style={{
      position: 'absolute', left: 0, right: 0, bottom: 120,
      display: 'flex', justifyContent: 'center',
      opacity: op, pointerEvents: interactable ? 'auto' : 'none'
    }}>
      <button
        onClick={onClick}
        style={{
          padding: '14px 60px',
          background: hexA(color, 0.12),
          border: `1.5px solid ${color}`,
          color,
          fontFamily: "'Chakra Petch', sans-serif",
          fontSize: 18, fontWeight: 700, letterSpacing: '0.15em',
          cursor: 'pointer',
          clipPath: 'polygon(12px 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%, 0 12px)',
          boxShadow: `0 0 24px ${hexA(color, 0.4)}, inset 0 0 12px ${hexA(color, 0.2)}`
        }}
      >
        {label}
      </button>
    </div>
  );
}

// ── ShieldIcon ───────────────────────────────────────────────────
// Файервол-щит. При melting>0 — верхние точки опускаются, две капли
// под щитом. glow — opacity внешнего ореола.
export function ShieldIcon({
  size = 100, color = '#FFB13D', melting = 0, glow = 0, x = 360, y = 480
}: {
  size?: number; color?: string; melting?: number; glow?: number;
  x?: number; y?: number;
}) {
  const drop = melting * 40;
  const shieldOp = Math.max(0, 1 - melting * 0.4);
  return (
    <div style={{
      position: 'absolute', left: x - size / 2, top: y - size / 2,
      width: size, height: size,
      filter: `drop-shadow(0 0 ${24 + glow * 30}px ${hexA(color, 0.5 + glow * 0.4)})`
    }}>
      <svg viewBox="0 0 100 100" width={size} height={size}>
        {/* shield path: 5-point */}
        <path
          d={`M 50 ${10 + drop} L 85 ${25 + drop} L 80 60 L 50 92 L 20 60 L 15 ${25 + drop} Z`}
          fill={hexA(color, 0.18)}
          stroke={color}
          strokeWidth="2"
          opacity={shieldOp}
        />
        {/* молния внутри */}
        <path
          d="M 50 30 L 38 56 L 48 56 L 42 76 L 60 46 L 50 46 L 56 30 Z"
          fill={color}
          opacity={shieldOp * 0.85}
        />
      </svg>
      {/* капли (только при melting>0.5) */}
      {melting > 0.5 && (
        <>
          <div style={{
            position: 'absolute', left: size * 0.35, top: size * 0.85 + drop * 0.3,
            width: 6, height: 10, borderRadius: '50%',
            background: color, opacity: melting * 0.6
          }} />
          <div style={{
            position: 'absolute', left: size * 0.6, top: size * 0.9 + drop * 0.5,
            width: 4, height: 8, borderRadius: '50%',
            background: color, opacity: melting * 0.4
          }} />
        </>
      )}
    </div>
  );
}

// ── HexShield ────────────────────────────────────────────────────
// Маленький шестиугольный файервол (для счётчика «осталось N»).
export function HexShield({ filled, color }: { filled: boolean; color: string }) {
  return (
    <div style={{
      width: 28, height: 32,
      background: filled ? color : 'transparent',
      border: `1.5px solid ${filled ? color : hexA(color, 0.3)}`,
      clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
      boxShadow: filled ? `0 0 8px ${hexA(color, 0.5)}` : 'none'
    }} />
  );
}

// ── TopCaption ───────────────────────────────────────────────────
// Маленькая верхняя надпись «⟢ ИЗМЕНЕНИЕ ЛИГИ ⟣», JetBrains Mono.
export function TopCaption({
  at = 0.1, text, color = '#A0A4B4', glow = false
}: { at?: number; text: string; color?: string; glow?: boolean }) {
  const t = useTime();
  const op = T(t, at, at + 0.5, 0, 1, Easing.easeOutCubic);
  return (
    <div style={{
      position: 'absolute', left: 0, right: 0, top: 80,
      textAlign: 'center', opacity: op,
      color, fontFamily: "'JetBrains Mono', monospace",
      fontSize: 12, letterSpacing: '0.25em', fontWeight: 500,
      textShadow: glow ? `0 0 16px ${color}` : 'none',
      pointerEvents: 'none'
    }}>{text}</div>
  );
}

// ── helpers ──────────────────────────────────────────────────────

// HEX → rgba string с заданным alpha.
export function hexA(hex: string, a: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}
